import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@elkaso-app/web-design';
import type { CmsRestaurantRow } from 'apis/restaurants/types';
import { useAssignAccountManagerApi } from 'apis/restaurants/use-assign-account-manager-api';
import {
  AccountManagerOption,
  useSearchAccountManagerCandidatesApi,
} from 'apis/restaurants/use-search-account-manager-candidates-api';
import { useUnassignAccountManagerApi } from 'apis/restaurants/use-unassign-account-manager-api';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

export interface IAssignAccountManagerModal {
  isOpen: boolean;
  onClose: () => void;
  restaurant: CmsRestaurantRow;
}

type TInputs = {
  accountManager: AccountManagerOption | null;
};

const formatAssignedSince = (accountManager: NonNullable<CmsRestaurantRow['accountManager']>) => {
  const since = new Date(accountManager.assignedAt).toLocaleDateString();
  return accountManager.assignedBy ? `Since ${since} by ${accountManager.assignedBy.name}` : `Since ${since}`;
};

// Assign / change / remove the account manager of one restaurant. Mounted by the row actions
// cell (like BulkUploadModal); the list refetches through the mutation hooks' invalidation.
export const AssignAccountManagerModal = ({ isOpen, onClose, restaurant }: IAssignAccountManagerModal) => {
  const current = restaurant.accountManager;
  const { loadOptions } = useSearchAccountManagerCandidatesApi();
  const assignAccountManager = useAssignAccountManagerApi();
  const unassignAccountManager = useUnassignAccountManagerApi();
  const [confirmRemove, setConfirmRemove] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<TInputs>({ defaultValues: { accountManager: null } });

  useEffect(() => {
    if (isOpen) {
      reset({ accountManager: null });
      setConfirmRemove(false);
    }
  }, [isOpen, reset]);

  const selected = watch('accountManager');
  const selectedUserId = typeof selected?.value === 'number' ? selected.value : null;
  const canSave = selectedUserId !== null && selectedUserId !== current?.id;
  const isBusy = assignAccountManager.isLoading || unassignAccountManager.isLoading;

  const onSubmit = async () => {
    if (!canSave || isBusy || selectedUserId === null) return;
    try {
      await assignAccountManager.mutateAsync({ restaurantId: restaurant.id, userId: selectedUserId });
      onClose();
    } catch {
      // error toast handled by the mutation hook
    }
  };

  const onRemove = async () => {
    try {
      await unassignAccountManager.mutateAsync({ restaurantId: restaurant.id });
      onClose();
    } catch {
      // error toast handled by the mutation hook
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'md' }}>
      <ModalOverlay />
      <ModalContent as='form' onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>Account Manager: {restaurant.nameEn}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align='stretch' spacing='md'>
            <VStack align='start' spacing='0'>
              <Text fontSize='sm' color='gray.500'>
                Current
              </Text>
              {current ? (
                <>
                  <Text fontWeight='semibold'>
                    {current.email ? `${current.name} (${current.email})` : current.name}
                  </Text>
                  <Text fontSize='xs' color='gray.500'>
                    {formatAssignedSince(current)}
                  </Text>
                </>
              ) : (
                <Text color='gray.400'>Unassigned</Text>
              )}
            </VStack>

            <Controller
              control={control}
              name='accountManager'
              render={({ field: { onChange, name, onBlur, value } }) => (
                <FormControl>
                  <FormLabel fontSize='sm'>{current ? 'Change to' : 'Assign'}</FormLabel>
                  <ChakraAsyncSelect
                    id='accountManager'
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    value={value}
                    loadOptions={loadOptions}
                    defaultOptions
                    cacheOptions
                    isClearable
                    placeholder='Search active CMS users'
                  />
                </FormControl>
              )}
            />

            {confirmRemove && current && (
              <Text fontSize='sm' color='red.500'>
                Remove {current.name} as account manager of {restaurant.nameEn}?
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap='2'>
          {current && !confirmRemove && (
            <Button
              variant='ghost'
              colorScheme='red'
              me='auto'
              onClick={() => setConfirmRemove(true)}
              isDisabled={isBusy}>
              Remove
            </Button>
          )}
          {current && confirmRemove && (
            <Button
              colorScheme='red'
              me='auto'
              onClick={onRemove}
              isLoading={unassignAccountManager.isLoading}
              isDisabled={assignAccountManager.isLoading}>
              Confirm remove
            </Button>
          )}
          <Button variant='outline' onClick={onClose} isDisabled={isBusy}>
            Cancel
          </Button>
          <Button
            colorScheme='red'
            type='submit'
            isDisabled={!canSave || isBusy}
            isLoading={assignAccountManager.isLoading}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
